/**
 * Converts various date input formats to a JavaScript `Date` object, optionally converting to a specified timezone.
 *
 * NOTE! When converting to a timezone, the date is converted to the timezone of the client it's run on.
 * So the UTC of the Date resulting date object will not match the original UTC date, but the date will be
 * presentable in the client's timezone. E.g. 09:30 UTC will be 10:30 in Stockholm with timezone "Europe/Stockholm" but
 * the UTC of the Date object will be 10:30 in London with timezone "Europe/Stockholm" to match
 * the client's timezone but still present the time in "Europe/Stockholm" timezone.
 *
 * @param input - The date input to be converted. It can be one of the following types:
 * - `Date`: A JavaScript `Date` object.
 * - `IsoTimestamp`: A string in ISO 8601 format.
 * - `WithIsoTimestamp`: An object containing an `iso` property with a string in ISO 8601 format.
 * - `WithUnixTimestamp`: An object containing a `unix` property with a Unix timestamp.
 * - `IsoAndUnixTimestamp`: An object containing both `iso` and `unix` properties.
 * - `FullDate`: A custom date type.
 * - `null`: A null value.
 * - `undefined`: An undefined value.
 *
 * @param timezone - An optional timezone string. If provided, the date will be converted to this timezone.
 *
 * @returns A JavaScript `Date` object representing the input date, optionally converted to the specified timezone. If the input is `null` or `undefined`, the function returns `undefined`.
 */
import { toZonedTime, zoneDateUtils, ZonedDate } from "@@/utilities/zoned-date";
import { parse, parseISO } from "date-fns";
import type { FullDate } from "./full-date";
import { isDate } from "./is-date";
import type {
    IsoAndUnixTimestamp,
    IsoTimestamp,
    WithIsoTimestamp,
    WithUnixTimestamp,
} from "./iso-and-unix";
import type { Timezone } from "./timezone";
import { fromUnix } from "./unix";

function asDate(input: Date): Date;
function asDate(input: Date, timezone: Timezone): ZonedDate;
function asDate(input: Date | IsoTimestamp): Date;
function asDate(input: Date | IsoTimestamp, timezone: Timezone): ZonedDate;
function asDate(input: WithIsoTimestamp): Date;
function asDate(input: WithIsoTimestamp, timezone: Timezone): ZonedDate;
function asDate(input: WithUnixTimestamp): Date;
function asDate(input: WithUnixTimestamp, timezone: Timezone): ZonedDate;
function asDate(input: Date | WithUnixTimestamp): Date;
function asDate(input: Date | WithUnixTimestamp, timezone: Timezone): ZonedDate;
function asDate(input: IsoAndUnixTimestamp): Date;
function asDate(input: IsoAndUnixTimestamp, timezone: Timezone): ZonedDate;
function asDate(input: IsoTimestamp): Date;
function asDate(input: IsoTimestamp, timezone: Timezone): ZonedDate;
function asDate(input: IsoAndUnixTimestamp | Date): Date;
function asDate(
    input: IsoAndUnixTimestamp | Date,
    timezone: Timezone,
): ZonedDate;
function asDate(input: FullDate): Date;
function asDate(input: FullDate, timezone: Timezone): ZonedDate;
function asDate(input: FullDate | IsoAndUnixTimestamp): Date;
function asDate(
    input: FullDate | IsoAndUnixTimestamp,
    timezone: Timezone,
): ZonedDate;
function asDate(input: undefined, timezone?: Timezone): undefined;
function asDate(input: null, timezone?: Timezone): undefined;
function asDate(input: IsoAndUnixTimestamp | undefined): Date | undefined;
function asDate(
    input: IsoAndUnixTimestamp | undefined,
    timezone: Timezone,
): ZonedDate | undefined;
function asDate(
    input:
        | WithIsoTimestamp
        | WithUnixTimestamp
        | IsoAndUnixTimestamp
        | IsoTimestamp
        | Date
        | FullDate
        | null
        | undefined,
): Date | undefined;
function asDate(
    input:
        | WithIsoTimestamp
        | WithUnixTimestamp
        | IsoAndUnixTimestamp
        | IsoTimestamp
        | Date
        | FullDate
        | null
        | undefined,
    timezone: Timezone,
): ZonedDate | undefined;
function asDate(
    input:
        | WithIsoTimestamp
        | WithUnixTimestamp
        | IsoAndUnixTimestamp
        | IsoTimestamp
        | Date
        | FullDate
        | null
        | undefined,
    timezone?: Timezone,
): Date | ZonedDate | undefined {
    // Timezone is used when parsing a FullDate
    // cause they don't have timezone data
    const parsedDate = parseDate(input);
    if (parsedDate && timezone) return toZonedTime(parsedDate, timezone);
    return parsedDate;
}

const parseDate = (input: Parameters<typeof asDate>[0]): Date | undefined => {
    if (typeof input === "undefined") return undefined;
    if (input === null) return undefined;
    if (input instanceof ZonedDate) {
        return zoneDateUtils.fromZonedDate(input);
    }
    if (isDate(input)) return input;
    if (typeof input === "number") {
        return parse(input.toString(), "yyyyMMdd", new Date());
    }
    if (typeof input === "string") return parseISO(input);
    if ("iso" in input) return parseISO(input.iso);
    if ("unix" in input) return fromUnix(input.unix);
    return undefined;
};

export { asDate };
